'use client';

import { createContext, type ReactNode, use, useCallback, useMemo, useState } from 'react';

import { LockedContentUpgradeModal } from '../components/locked-content-upgrade-modal/locked-content-upgrade-modal';
import { useLocation } from '../hooks/use-navigation';

/** Locked catalogue items that open the contextual upgrade modal instead of routing to pricing. */
export type LockedContentKind = 'document-template' | 'email-template' | 'deal' | 'workshop';

export interface LockedContentUpgradeRequest {
  kind: LockedContentKind;
  title: string;
  returnTo: string;
  returnOrigin?: string;
  /** The control the user activated; focus returns here when the modal closes. */
  opener: HTMLElement | null;
  onReplay?: () => void;
}

interface LockedContentUpgradeContextValue {
  openLockedContentUpgrade: (request: LockedContentUpgradeRequest) => void;
}

const LockedContentUpgradeContext = createContext<LockedContentUpgradeContextValue | null>(null);

/**
 * `null` outside a provider. Callers then keep their previous behaviour
 * (routing to pricing), so surfaces without the provider — landing, admin —
 * are unaffected.
 */
export const useLockedContentUpgrade = (): LockedContentUpgradeContextValue | null => use(LockedContentUpgradeContext);

/**
 * Hosts the single upgrade modal for locked templates and deals. It is mounted
 * once at the app shell rather than inside each card: React synthetic events
 * bubble through portals, so a modal rendered inside a clickable card would
 * re-trigger that card's handler on every click inside the dialog.
 *
 * Holds no entitlement state. Whether an item is locked is decided by
 * `useContentLockState`; the backend remains the access boundary.
 */
export function LockedContentUpgradeProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<LockedContentUpgradeRequest | null>(null);
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const { pathname } = useLocation();

  const openLockedContentUpgrade = useCallback(
    (next: LockedContentUpgradeRequest) => {
      setRequest(next);
      setOpenedAt(pathname);
    },
    [pathname],
  );

  const close = useCallback(() => setOpenedAt(null), []);

  // Open only on the page it was opened from: following the upgrade link, or any
  // other navigation, must never leave the dialog behind.
  const isOpen = openedAt === pathname;

  const value = useMemo(() => ({ openLockedContentUpgrade }), [openLockedContentUpgrade]);

  return (
    <LockedContentUpgradeContext.Provider value={value}>
      {children}
      {request && <LockedContentUpgradeModal request={request} isOpen={isOpen} onClose={close} />}
    </LockedContentUpgradeContext.Provider>
  );
}
